const EDGE_PUNCTUATION_RE = /^[\s"'“”‘’([{]+|[\s"'“”‘’.,!?;:)\]}]+$/g;

export function normalizeAssistantText(text: string): string {
  return collapseIncrementalSnapshots(text).trim();
}

export function mergeAssistantTextPartial(previous: string, incoming: string): string {
  const existing = normalizeAssistantText(previous);
  const next = normalizeAssistantText(incoming);
  if (!existing) return next;
  if (!next) return existing;
  if (next === existing || existing.includes(next)) return existing;
  if (next.startsWith(existing)) return next;

  const overlap = longestTextOverlap(existing, next);
  if (overlap > 0) {
    return normalizeAssistantText(`${existing}${next.slice(overlap)}`);
  }

  const separator =
    /\s$/.test(existing) || /^\s|^[.,!?;:)]/.test(next) ? "" : " ";
  return normalizeAssistantText(`${existing}${separator}${next}`);
}

export function collapseIncrementalSnapshots(text: string): string {
  const tokens = text.trim().split(/\s+/);
  if (tokens.length < 4) return text;

  const first = comparableToken(tokens[0] ?? "");
  if (!first) return text;

  let bestStart = -1;
  for (let start = 1; start < tokens.length - 1; start++) {
    if (comparableToken(tokens[start] ?? "") !== first) continue;
    if (isPrefixSnapshotRun(tokens, start)) bestStart = start;
  }

  if (bestStart <= 0) return text;
  const collapsed = tokens.slice(bestStart).join(" ");
  return collapsed.length < text.trim().length ? collapsed : text;
}

function isPrefixSnapshotRun(tokens: string[], finalStart: number): boolean {
  if (tokens.slice(0, finalStart).some(hasSentenceEndingPunctuation)) {
    return false;
  }

  const finalTokens = tokens.slice(finalStart);
  let index = 0;
  let segments = 0;
  let previousLength = 0;
  let grew = false;

  while (index < finalStart) {
    const length = longestPrefixLength(tokens, index, finalStart, finalTokens);
    if (length === 0) return false;

    if (length < previousLength) return false;
    if (segments > 0 && length > previousLength) grew = true;
    previousLength = length;
    segments += 1;
    index += length;
  }

  return segments >= 2 && grew;
}

function longestPrefixLength(
  tokens: string[],
  index: number,
  finalStart: number,
  finalTokens: string[],
): number {
  const maxLength = Math.min(finalStart - index, finalTokens.length);
  for (let length = maxLength; length > 0; length--) {
    let matches = true;
    for (let offset = 0; offset < length; offset++) {
      if (
        comparableToken(tokens[index + offset] ?? "") !==
        comparableToken(finalTokens[offset] ?? "")
      ) {
        matches = false;
        break;
      }
    }
    if (matches) return length;
  }
  return 0;
}

function comparableToken(token: string): string {
  return token.toLocaleLowerCase().replace(EDGE_PUNCTUATION_RE, "");
}

function hasSentenceEndingPunctuation(token: string): boolean {
  return /[.!?][)"'”’\]}]*$/.test(token.trim());
}

function longestTextOverlap(previous: string, incoming: string): number {
  const maxLength = Math.min(previous.length, incoming.length);
  for (let length = maxLength; length >= 4; length--) {
    if (!previous.endsWith(incoming.slice(0, length))) continue;
    if (!/\s/.test(incoming.slice(0, length))) continue;
    return length;
  }
  return 0;
}
